// ─── Parse cron_string once and normalize day‐codes ───────────────────────

import { MAX_INTERVAL, MIN_INTERVAL } from '../constants/SchedulingConstants';

/**
 * CronParts
 * ---------
 * Interface representing parsed parts of a cron expression.
 */
export interface CronParts {
  /** Minute field (0–59) */
  minute: string;
  /** Hour field (0–23) */
  hour: string;
  /** Interval for repetition (e.g. every N weeks) */
  interval: number;
}

/**
 * parseCron
 * ---------
 * Parses a Quartz-style cron expression for weekly scheduling.
 *
 * @param {string} cron - Full cron string (e.g. "0 15 10 1/28 * ? *").
 * @returns {CronParts}
 */
export function parseCron(cron: string): CronParts {
  const parts = cron.trim().split(/\s+/);
  // parts = [seconds, minute, hour, dayOfMonth, month, dayOfWeek, year]
  const [, minute, hour, dom] = parts;

  let interval = 1;

  // Parse weekly pattern: dayOfMonth is "1/N" where N = 7 * weeks
  if (dom.startsWith('1/')) {
    const days = parseInt(dom.split('/')[1], 10) || 7;

    interval = Math.max(1, Math.floor(days / 7));
  }

  return { minute, hour, interval };
}

/**
 * buildCron
 * ---------
 * Constructs a Quartz-style cron expression for weekly scheduling.
 *
 * @param {CronParts} parts - Parsed cron parts
 * @returns {string} Full cron string
 */
export function buildCron({ minute, hour, interval }: CronParts): string {
  const sec = '0';
  const days = 7 * interval;

  // Weekly pattern: run every N weeks (N*7 days) starting from day 1
  return `${sec} ${minute} ${hour} 1/${days} * ? *`;
}

/**
 * parseSchedulingCron
 * -------------------
 * Adapts parseCron output for UI usage.
 *
 * @param {string} cronString - Original cron expression
 * @returns {{ minute: string; hour: string; interval: number }}
 */
export function parseSchedulingCron(cronString: string) {
  const { minute, hour, interval } = parseCron(cronString);

  return { minute, hour, interval };
}

/**
 * formatTimeLabel
 * ---------------
 * Converts hour/minute values into a zero-padded string for display.
 *
 * @param {number} hour   - Hour of day (0–23)
 * @param {number} minute - Minute of hour (0–59)
 * @returns {string}      - Formatted "HH:MM"
 */
export function formatTimeLabel(hour: number, minute: number): string {
  const hh = String(hour).padStart(2, '0');
  const mm = String(minute).padStart(2, '0');

  return `${hh}:${mm}`;
}

/**
 * clampInterval
 * -------------
 * Clamps a numeric value to the allowed range.
 * @param raw - Raw numeric value from user input
 * @returns number - Clamped between MIN_INTERVAL and MAX_INTERVAL
 */
export function clampInterval(raw: number): number {
  if (Number.isNaN(raw)) {
    return MIN_INTERVAL;
  }

  return Math.min(Math.max(raw, MIN_INTERVAL), MAX_INTERVAL);
}
